import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from './useAuth.js';

export interface AuthGuardProps {
  readonly children: ReactNode;
  readonly redirectTo?: string;
}

export function AuthGuard({ children, redirectTo = '/login' }: AuthGuardProps): JSX.Element | null {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading || isAuthenticated) return;
    void navigate({ to: redirectTo, replace: true });
  }, [isAuthenticated, isLoading, navigate, redirectTo]);

  if (isLoading || !isAuthenticated) {
    return <AuthSpinner />;
  }
  return <>{children}</>;
}

function AuthSpinner(): JSX.Element {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--color-bg)',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          border: '2px solid var(--color-border)',
          borderTopColor: 'var(--color-accent)',
          animation: 'auth-spin 0.9s linear infinite',
        }}
      />
      <style>{`@keyframes auth-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
